import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/api';
import {
  Company,
  CompanyUser,
  CompanyBranding,
  DeleteConfirmation,
  CreateCompanyPayload,
  CreateCompanyUserPayload,
  CompanyLock,
  CompanyGroup,
  LockAssignment,
  GroupAssignment,
  CompanyUsersImportResult,
} from '../types';
import {
  Building2,
  Plus,
  Trash2,
  Users,
  ChevronLeft,
  Edit3,
  Key,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  X,
  Shield,
  UserPlus,
  Settings,
  Palette,
  ToggleLeft,
  ToggleRight,
  Filter,
  Upload,
  FileText,
  CreditCard,
  Download,
} from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import NfcCardsManager from './NfcCardsManager';
import Modal from './ui/Modal';

const IMPORT_CHUNK_SIZE = 1000;
const COMPANY_USERS_PAGE_SIZE = 200;

// Jerarquía de planes (refleja el backend). El gate real lo aplica la API;
// esto solo evita que el usuario rellene el formulario para acabar en un 403.
const TIER_RANK: Record<string, number> = { free: 0, basic: 1, pro: 2, business: 3, enterprise: 4 };
// Debe coincidir con COMPANY_MIN_TIER del .env del backend (default: business).
const MIN_COMPANY_TIER = 'business';

type CsvRow = {
  cells: string[];
  originalRow: number;
};

const detectCsvDelimiter = (text: string): ',' | ';' => {
  const firstLine = text.split(/\r?\n/, 1)[0] || '';
  const semicolons = (firstLine.match(/;/g) || []).length;
  const commas = (firstLine.match(/,/g) || []).length;
  return semicolons > commas ? ';' : ',';
};

const parseCsvRows = (text: string, delimiter: ',' | ';'): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (char === '"' && next === '"') {
        cell += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      row.push(cell);
      cell = '';
    } else if (char === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else if (char === '\r') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      if (next === '\n') i += 1;
    } else {
      cell += char;
    }
  }

  row.push(cell);
  if (row.some((value) => value.trim())) rows.push(row);
  return rows;
};

const isBlankCsvRow = (row: string[]) => row.every((cell) => !cell.trim());

// On/off switch used for access toggling.
const Switch: React.FC<{ on: boolean; onClick: () => void; disabled?: boolean; label?: string }> = ({ on, onClick, disabled, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    role="switch"
    aria-checked={on}
    aria-label={label}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${on ? 'bg-indigo-600' : 'bg-slate-300'}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${on ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

const serializeCsvRows = (rows: string[][]): string => {
  return rows
    .map((row) =>
      row
        .map((value) => {
          const cell = value ?? '';
          const needsQuotes = /[",\r\n]/.test(cell) || cell !== cell.trim();
          return needsQuotes ? `"${cell.replace(/"/g, '""')}"` : cell;
        })
        .join(',')
    )
    .join('\r\n');
};

// Downloadable example templates so admins have a CSV to overwrite with their own rows.
const USER_IMPORT_EXAMPLE_WITH_PASSWORD = [
  'username,password,first_name,last_name,role',
  'john.doe,Passw0rd1,John,Doe,user',
  'jane.smith,Str0ngPass,Jane,Smith,admin',
].join('\r\n');

const USER_IMPORT_EXAMPLE_CODE_ONLY = [
  'username,first_name,last_name,role',
  'acc-001,John,Doe,user',
  'acc-002,Jane,Smith,admin',
].join('\r\n');

// Triggers a client-side CSV download (BOM-prefixed so Excel reads UTF-8 correctly).
const downloadCsvTemplate = (filename: string, content: string) => {
  const blob = new Blob([`﻿${content}\r\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ─── Sub-views ───────────────────────────────────────────────
type View =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'detail'; company: Company }
  | { kind: 'edit'; company: Company }
  | { kind: 'addUser'; company: Company }
  | { kind: 'manageLocks'; company: Company; user: CompanyUser };

const CompanyManagement: React.FC = () => {
  // ── state ──────────────────────────────────────────────────
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [companyUsersTotal, setCompanyUsersTotal] = useState(0);
  const [companyUsersHasMore, setCompanyUsersHasMore] = useState(false);
  const [companyUsersLoading, setCompanyUsersLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>({ kind: 'list' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // subscription tier (gates company creation: business+)
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);

  // create company form
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [newMaxUsers, setNewMaxUsers] = useState<string>('');
  const [newBranding, setNewBranding] = useState<CompanyBranding>({});
  const [newLoginMode, setNewLoginMode] = useState<'password' | 'code_only'>('password');

  // edit company form
  const [editName, setEditName] = useState('');
  const [editMaxUsers, setEditMaxUsers] = useState<string>('');
  const [editBranding, setEditBranding] = useState<CompanyBranding>({});
  const [editIsActive, setEditIsActive] = useState(true);
  const [editLoginMode, setEditLoginMode] = useState<'password' | 'code_only'>('password');

  // create user form
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newRole, setNewRole] = useState<'user' | 'admin'>('user');
  const [showNewPassword, setShowNewPassword] = useState(false);

  // reset password
  const [resetUserId, setResetUserId] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);

  // CSV user import
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importDryRun, setImportDryRun] = useState(true);
  const [importUpdateExisting, setImportUpdateExisting] = useState(false);
  const [importResult, setImportResult] = useState<CompanyUsersImportResult | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStage, setImportStage] = useState<'idle' | 'reading' | 'uploading' | 'processing'>('idle');
  const [importBatchLabel, setImportBatchLabel] = useState('');
  const [importPreviewRows, setImportPreviewRows] = useState<number | null>(null);

  // lock assignment
  const [availableLocks, setAvailableLocks] = useState<CompanyLock[]>([]);
  const [availableGroups, setAvailableGroups] = useState<CompanyGroup[]>([]);
  const [assignedUnits, setAssignedUnits] = useState<LockAssignment[]>([]);
  const [assignedGroups, setAssignedGroups] = useState<GroupAssignment[]>([]);
  const [locksLoading, setLocksLoading] = useState(false);

  const [confirmation, setConfirmation] = useState<DeleteConfirmation>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    onCancel: () => {},
  });
  const [actionLoading, setActionLoading] = useState(false);

  // filters
  const [companyFilter, setCompanyFilter] = useState('');
  const [companyUserSearch, setCompanyUserSearch] = useState('');
  const [lockAssignFilter, setLockAssignFilter] = useState('');
  const [detailTab, setDetailTab] = useState<'users' | 'access' | 'nfc'>('users');
  const [showImport, setShowImport] = useState(false);
  const [assignmentTab, setAssignmentTab] = useState<'groups' | 'locks'>('groups');
  const [bulkGroupId, setBulkGroupId] = useState('');
  const [bulkAllowSettings, setBulkAllowSettings] = useState(false);


  // ── view persistence (survive browser refresh) ──────────────
  const viewRestored = useRef(false);

  // ── loaders ────────────────────────────────────────────────
  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.getMyCompanies();
      if (res.success && res.data) setCompanies(res.data);
    } catch (e) {
      console.error('Error loading companies', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCompanyUsers = useCallback(async (companyId: string, search = '', offset = 0, append = false) => {
    try {
      setCompanyUsersLoading(true);
      const trimmedSearch = search.trim();
      const res = await apiService.getCompanyUsers(companyId, {
        search: trimmedSearch || undefined,
        limit: COMPANY_USERS_PAGE_SIZE,
        offset,
      });
      if (res.success && res.data) {
        const page = res.data;
        setCompanyUsers((prev) => (append ? [...prev, ...page.users] : page.users));
        setCompanyUsersTotal(page.total);
        setCompanyUsersHasMore(page.has_more);
      }
    } catch (e) {
      console.error('Error loading company users', e);
    } finally {
      setCompanyUsersLoading(false);
    }
  }, []);

  const loadCompanyGroups = useCallback(async (companyId: string) => {
    try {
      const res = await apiService.getCompanyGroups(companyId);
      if (res.success && res.data) setAvailableGroups(res.data);
    } catch (e) {
      console.error('Error loading company groups', e);
    }
  }, []);

  const loadLockAssignments = useCallback(async (companyId: string, userId: string) => {
    try {
      setLocksLoading(true);
      const [locksRes, groupsRes, assignmentsRes] = await Promise.all([
        apiService.getCompanyLocks(companyId),
        apiService.getCompanyGroups(companyId),
        apiService.getUserLockAssignments(companyId, userId),
      ]);
      if (locksRes.success && locksRes.data) setAvailableLocks(locksRes.data);
      if (groupsRes.success && groupsRes.data) setAvailableGroups(groupsRes.data);
      if (assignmentsRes.success && assignmentsRes.data) {
        setAssignedUnits(assignmentsRes.data.shared_units);
        setAssignedGroups(assignmentsRes.data.shared_groups);
      }
    } catch (e) {
      console.error('Error loading lock assignments', e);
    } finally {
      setLocksLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Load the owner's subscription tier to gate company creation (business+).
  useEffect(() => {
    apiService.getSubscriptionStatus().then((res) => {
      if (res.success && res.data) setSubscriptionTier(res.data.tier_id);
    });
  }, []);

  // Save view to sessionStorage on every navigation
  useEffect(() => {
    if (view.kind === 'list' || view.kind === 'create') {
      // Only clear once we've already restored - otherwise on refresh the initial
      // 'list' view would wipe the saved state before we get a chance to read it.
      if (viewRestored.current) sessionStorage.removeItem('lw_view');
      return;
    } else if (view.kind === 'detail') {
      sessionStorage.setItem('lw_view', JSON.stringify({ kind: 'detail', companyId: view.company.id }));
    } else if (view.kind === 'manageLocks') {
      sessionStorage.setItem('lw_view', JSON.stringify({ kind: 'manageLocks', companyId: view.company.id, userId: view.user.id }));
    } else if (view.kind === 'edit') {
      sessionStorage.setItem('lw_view', JSON.stringify({ kind: 'detail', companyId: view.company.id }));
    } else if (view.kind === 'addUser') {
      sessionStorage.setItem('lw_view', JSON.stringify({ kind: 'detail', companyId: view.company.id }));
    }
  }, [view]);

  // Restore view after companies load (once per session)
  useEffect(() => {
    if (companies.length === 0 || viewRestored.current) return;
    viewRestored.current = true;
    const saved = sessionStorage.getItem('lw_view');
    if (!saved) return;
    try {
      const state = JSON.parse(saved);
      const company = companies.find((c) => c.id === state.companyId);
      if (!company) return;
      if (state.kind === 'detail') {
        setView({ kind: 'detail', company });
      } else if (state.kind === 'manageLocks') {
        apiService.getCompanyUsers(company.id, { limit: 200 }).then((res) => {
          const users = res.data?.users || [];
          const user = users.find((u: CompanyUser) => u.id === state.userId);
          if (user) setView({ kind: 'manageLocks', company, user });
          else setView({ kind: 'detail', company });
        });
      }
    } catch {
      sessionStorage.removeItem('lw_view');
    }
  }, [companies]);

  useEffect(() => {
    if (view.kind === 'detail') {
      loadCompanyGroups(view.company.id);
    }
    if (view.kind === 'manageLocks') loadLockAssignments(view.company.id, view.user.id);
  }, [view, loadCompanyGroups, loadLockAssignments]);

  useEffect(() => {
    if (view.kind !== 'detail') return;
    const timeoutId = window.setTimeout(() => {
      loadCompanyUsers(view.company.id, companyUserSearch, 0, false);
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [view, companyUserSearch, loadCompanyUsers]);

  // ── helpers ────────────────────────────────────────────────
  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const showConfirmation = (title: string, message: string, onConfirm: () => void) => {
    setConfirmation({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        onConfirm();
        setConfirmation((p) => ({ ...p, isOpen: false }));
      },
      onCancel: () => setConfirmation((p) => ({ ...p, isOpen: false })),
    });
  };

  // ── actions ────────────────────────────────────────────────
  const handleImportFileChange = async (file: File | null) => {
    clearMessages();
    setImportFile(file);
    setImportResult(null);
    setImportProgress(0);
    setImportStage('idle');
    setImportBatchLabel('');
    setImportPreviewRows(null);

    if (!file) return;

    try {
      const text = (await file.text()).replace(/^\uFEFF/, '');
      const delimiter = detectCsvDelimiter(text);
      const csvRows = parseCsvRows(text, delimiter);
      const header = csvRows[0] || [];

      if (header.length === 0 || isBlankCsvRow(header)) {
        setError('CSV header is required');
        return;
      }

      const totalRows = csvRows.slice(1).filter((row) => !isBlankCsvRow(row)).length;
      setImportPreviewRows(totalRows);
    } catch (e) {
      console.error('Error reading CSV file', e);
      setError('Could not read CSV file');
    }
  };

  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    if (!newCode.trim() || !newName.trim()) {
      setError('Code and name are required');
      return;
    }
    if (newCode.trim().length < 3) {
      setError('Code must be at least 3 characters');
      return;
    }
    if (!/^[A-Za-z0-9-]+$/.test(newCode.trim())) {
      setError('Code can only contain letters, numbers and hyphens');
      return;
    }
    setActionLoading(true);
    const payload: CreateCompanyPayload = {
      code: newCode.trim(),
      name: newName.trim(),
      branding: Object.keys(newBranding).length > 0 ? newBranding : undefined,
      max_users: newMaxUsers ? parseInt(newMaxUsers, 10) : null,
      login_mode: newLoginMode,
    };
    const res = await apiService.createCompany(payload);
    setActionLoading(false);
    if (res.success) {
      setSuccess('Company created successfully');
      setNewCode('');
      setNewName('');
      setNewMaxUsers('');
      setNewBranding({});
      setNewLoginMode('password');
      await loadCompanies();
      setTimeout(() => setView({ kind: 'list' }), 800);
    } else {
      setError(res.message || 'Failed to create company');
    }
  };

  const handleUpdateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    if (view.kind !== 'edit') return;
    setActionLoading(true);
    const res = await apiService.updateCompany(view.company.id, {
      name: editName.trim() || undefined,
      branding: Object.keys(editBranding).length > 0 ? editBranding : undefined,
      max_users: editMaxUsers ? parseInt(editMaxUsers, 10) : undefined,
      is_active: editIsActive,
      login_mode: editLoginMode,
    });
    setActionLoading(false);
    if (res.success) {
      setSuccess('Company updated');
      await loadCompanies();
      setTimeout(() => setView({ kind: 'list' }), 800);
    } else {
      setError(res.message || 'Failed to update company');
    }
  };

  const handleDeleteCompany = (company: Company) => {
    showConfirmation(
      'Delete Company',
      `Are you sure you want to delete "${company.name}" (${company.code})? This will also deactivate all its users. This action cannot be undone.`,
      async () => {
        const res = await apiService.deleteCompany(company.id);
        if (res.success) {
          await loadCompanies();
          setView({ kind: 'list' });
        }
      },
    );
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    if (view.kind !== 'addUser') return;
    const isPasswordMode = (view.company.login_mode ?? 'password') === 'password';
    if (!newUsername.trim() || (isPasswordMode && !newPassword)) {
      setError(isPasswordMode ? 'Username and password are required' : 'Access code is required');
      return;
    }
    setActionLoading(true);
    const payload: CreateCompanyUserPayload = {
      username: newUsername.trim(),
      password: isPasswordMode ? newPassword : undefined,
      first_name: newFirstName.trim() || undefined,
      last_name: newLastName.trim() || undefined,
      role: newRole,
    };
    const res = await apiService.createCompanyUser(view.company.id, payload);
    setActionLoading(false);
    if (res.success) {
      setSuccess(`User "${newUsername}" created`);
      setNewUsername('');
      setNewPassword('');
      setNewFirstName('');
      setNewLastName('');
      setNewRole('user');
      setTimeout(() => {
        setView({ kind: 'detail', company: view.company });
      }, 800);
    } else {
      setError(res.message || 'Failed to create user');
    }
  };

  const handleDeleteUser = (companyId: string, user: CompanyUser) => {
    showConfirmation(
      'Delete User',
      `Are you sure you want to delete user "${user.company_username}" (${user.full_name})? This action cannot be undone.`,
      async () => {
        const res = await apiService.deleteCompanyUser(companyId, user.id);
        if (res.success) loadCompanyUsers(companyId, companyUserSearch, 0, false);
      },
    );
  };

  const handleResetPassword = async (companyId: string) => {
    if (!resetUserId || !resetPassword) return;
    clearMessages();
    setActionLoading(true);
    const res = await apiService.resetCompanyUserPassword(companyId, resetUserId, resetPassword);
    setActionLoading(false);
    if (res.success) {
      setSuccess('Password reset successfully');
      setResetUserId(null);
      setResetPassword('');
    } else {
      setError(res.message || 'Failed to reset password');
    }
  };

  // ── lock assignment actions ────────────────────────────────
  const handleImportUsers = async (companyId: string) => {
    if (!importFile) {
      setError('Select a CSV file first');
      return;
    }
    clearMessages();
    setImportResult(null);
    setImportLoading(true);
    setImportProgress(0);
    setImportStage(importDryRun ? 'uploading' : 'reading');
    setImportBatchLabel('');
    setActionLoading(true);

    try {
      if (importDryRun) {
        const res = await apiService.importCompanyUsers(companyId, importFile, {
          dryRun: true,
          updateExisting: importUpdateExisting,
          onProgress: (percent) => {
            setImportProgress(percent);
            if (percent >= 100) setImportStage('processing');
          },
        });

        if (!res.success || !res.data) {
          setImportProgress(0);
          setError(res.message || 'Failed to import users');
          return;
        }

        setImportProgress(100);
        setImportResult(res.data);
        setSuccess(`CSV checked: ${res.data.total_rows} rows, ${res.data.errors.length} errors`);
        return;
      }

      const text = (await importFile.text()).replace(/^\uFEFF/, '');
      const delimiter = detectCsvDelimiter(text);
      const csvRows = parseCsvRows(text, delimiter);
      const header = csvRows[0]?.map((cell, index) => (index === 0 ? cell.replace(/^\uFEFF/, '') : cell)) || [];

      if (header.length === 0 || isBlankCsvRow(header)) {
        setImportProgress(0);
        setError('CSV header is required');
        return;
      }

      const usernameColumnIndex = header.findIndex((cell) => {
        const normalized = cell.trim().toLowerCase();
        return normalized === 'username' || normalized === 'company_username';
      });

      if (usernameColumnIndex < 0) {
        setImportProgress(0);
        setError('CSV must include username or company_username column');
        return;
      }

      const allRows: CsvRow[] = csvRows
        .slice(1)
        .map((cells, index) => ({ cells, originalRow: index + 2 }))
        .filter((row) => !isBlankCsvRow(row.cells));
      setImportPreviewRows(allRows.length);

      if (allRows.length === 0) {
        setImportProgress(0);
        setError('CSV does not contain any users');
        return;
      }

      const seenUsernames = new Set<string>();
      const duplicateErrors: CompanyUsersImportResult['errors'] = [];
      const importRows = allRows.filter((row) => {
        const username = (row.cells[usernameColumnIndex] || '').trim().toLowerCase();
        if (!/^[a-z0-9._-]{3,50}$/.test(username)) return true;
        if (seenUsernames.has(username)) {
          duplicateErrors.push({
            row: row.originalRow,
            username,
            error: 'Duplicate username in CSV',
          });
          return false;
        }
        seenUsernames.add(username);
        return true;
      });

      const chunks: CsvRow[][] = [];
      for (let i = 0; i < importRows.length; i += IMPORT_CHUNK_SIZE) {
        chunks.push(importRows.slice(i, i + IMPORT_CHUNK_SIZE));
      }

      // All-or-nothing across chunks: a single-request import is already atomic
      // server-side, but a multi-chunk import could half-commit and then hit the
      // plan limit. Validate the WHOLE file first (dry-run); if it would exceed
      // the limit (success === false), abort before writing anything.
      if (chunks.length > 1) {
        const preflight = await apiService.importCompanyUsers(companyId, importFile, {
          dryRun: true,
          updateExisting: importUpdateExisting,
        });
        if (!preflight.success) {
          setImportProgress(0);
          setError(preflight.message || 'Import would exceed your plan limit. No users were imported.');
          return;
        }
      }

      const aggregate: CompanyUsersImportResult = {
        dry_run: false,
        created: 0,
        updated: 0,
        skipped: duplicateErrors.length,
        errors: duplicateErrors,
        total_rows: allRows.length,
      };

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
        const chunk = chunks[chunkIndex];
        const chunkNumber = chunkIndex + 1;
        setImportStage('uploading');
        setImportBatchLabel(`Batch ${chunkNumber}/${chunks.length}`);

        const chunkCsv = serializeCsvRows([header, ...chunk.map((row) => row.cells)]);
        const chunkFileName = importFile.name.replace(/\.csv$/i, '') || 'company-users';
        const chunkFile = new File([chunkCsv], `${chunkFileName}-part-${chunkNumber}.csv`, { type: 'text/csv;charset=utf-8' });
        const res = await apiService.importCompanyUsers(companyId, chunkFile, {
          dryRun: false,
          updateExisting: importUpdateExisting,
          onProgress: (percent) => {
            const progress = Math.round(((chunkIndex + percent / 100) / chunks.length) * 100);
            setImportProgress(Math.min(progress, 99));
            if (percent >= 100) setImportStage('processing');
          },
        });

        if (!res.success || !res.data) {
          setImportResult(aggregate.errors.length || aggregate.created || aggregate.updated || aggregate.skipped ? aggregate : null);
          setError(res.message || `Failed to import users in batch ${chunkNumber}/${chunks.length}`);
          return;
        }

        aggregate.created += res.data.created;
        aggregate.updated += res.data.updated;
        aggregate.skipped += res.data.skipped;
        aggregate.errors.push(
          ...res.data.errors.map((err) => {
            const originalRow = chunk[err.row - 2]?.originalRow;
            return {
              ...err,
              row: originalRow || err.row,
            };
          })
        );
        setImportProgress(Math.round((chunkNumber / chunks.length) * 100));
      }

      setImportProgress(100);
      setImportResult(aggregate);
      setSuccess(`CSV imported: ${aggregate.created} created, ${aggregate.updated} updated, ${aggregate.skipped} skipped`);
      setImportFile(null);
      setImportPreviewRows(null);
      await loadCompanyUsers(companyId, companyUserSearch, 0, false);
    } finally {
      setActionLoading(false);
      setImportLoading(false);
      setImportStage('idle');
      setImportBatchLabel('');
    }
  };

  const handleAssignGroupToAllUsers = async (companyId: string) => {
    if (!bulkGroupId) {
      setError('Select a group first');
      return;
    }
    clearMessages();
    setActionLoading(true);
    const res = await apiService.assignGroupToAllCompanyUsers(companyId, bulkGroupId, bulkAllowSettings);
    setActionLoading(false);
    if (res.success && res.data) {
      setSuccess(
        `${res.data.group_name}: assigned to ${res.data.assigned_count}/${res.data.total_users} users (${res.data.created_count} new, ${res.data.reactivated_count} reactivated, ${res.data.skipped_count} skipped)`
      );
    } else {
      setError(res.message || 'Failed to assign group to all users');
    }
  };

  const handleUnassignGroupFromAllUsers = async (companyId: string) => {
    if (!bulkGroupId) {
      setError('Select a group first');
      return;
    }
    const selectedGroup = availableGroups.find((g) => g.id === bulkGroupId);
    showConfirmation(
      'Remove group from all users',
      `Remove "${selectedGroup?.name || 'this group'}" from every user in this company?`,
      async () => {
        clearMessages();
        setActionLoading(true);
        const res = await apiService.unassignGroupFromAllCompanyUsers(companyId, bulkGroupId);
        setActionLoading(false);
        if (res.success && res.data) {
          setSuccess(`${res.data.group_name}: removed from ${res.data.removed_count} users`);
        } else {
          setError(res.message || 'Failed to remove group from all users');
        }
      }
    );
  };

  const handleAssignLock = async (companyId: string, userId: string, unitId: string, allowSettings: boolean) => {
    clearMessages();
    setActionLoading(true);
    const res = await apiService.assignLockToUser(companyId, userId, {
      unit_id: unitId,
      allow_settings_changes: allowSettings,
    });
    setActionLoading(false);
    if (res.success) {
      setSuccess(res.data?.message || 'Lock assigned');
      await loadLockAssignments(companyId, userId);
    } else {
      setError(res.message || 'Failed to assign lock');
    }
  };

  const handleAssignGroup = async (companyId: string, userId: string, groupId: string, allowSettings: boolean) => {
    clearMessages();
    setActionLoading(true);
    const res = await apiService.assignLockToUser(companyId, userId, {
      group_id: groupId,
      allow_settings_changes: allowSettings,
    });
    setActionLoading(false);
    if (res.success) {
      setSuccess(res.data?.message || 'Group assigned');
      await loadLockAssignments(companyId, userId);
    } else {
      setError(res.message || 'Failed to assign group');
    }
  };

  const handleUnassignLock = async (companyId: string, userId: string, assignmentId: string, name: string) => {
    showConfirmation(
      'Remove Lock',
      `Remove lock "${name}" from this user? They will lose access on next sync.`,
      async () => {
        const res = await apiService.unassignLockFromUser(companyId, userId, assignmentId, 'unit');
        if (res.success) {
          setSuccess('Lock removed');
          await loadLockAssignments(companyId, userId);
        } else {
          setError(res.message || 'Failed to remove lock');
        }
      },
    );
  };

  const handleUnassignGroup = async (companyId: string, userId: string, assignmentId: string, name: string) => {
    showConfirmation(
      'Remove Group',
      `Remove group "${name}" from this user? They will lose access to all locks in the group.`,
      async () => {
        const res = await apiService.unassignLockFromUser(companyId, userId, assignmentId, 'group');
        if (res.success) {
          setSuccess('Group removed');
          await loadLockAssignments(companyId, userId);
        } else {
          setError(res.message || 'Failed to remove group');
        }
      },
    );
  };

  const handleToggleSettings = async (
    companyId: string, userId: string, assignmentId: string,
    current: boolean, type: 'unit' | 'group'
  ) => {
    clearMessages();
    const res = await apiService.updateLockAssignment(companyId, userId, assignmentId, !current, type);
    if (res.success) {
      await loadLockAssignments(companyId, userId);
    } else {
      setError(res.message || 'Failed to update settings permission');
    }
  };

  // ── Open edit view with pre-populated data ─────────────────
  const openEditView = (company: Company) => {
    setEditName(company.name);
    setEditMaxUsers(company.max_users != null ? String(company.max_users) : '');
    setEditBranding(company.branding || {});
    setEditIsActive(company.is_active);
    setEditLoginMode(company.login_mode ?? 'password');
    clearMessages();
    setView({ kind: 'edit', company });
  };

  const loginModeLabel = (mode?: 'password' | 'code_only') =>
    (mode ?? 'password') === 'code_only' ? 'Code only' : 'Username + password';

  const loginModeIcon = (mode?: 'password' | 'code_only') =>
    (mode ?? 'password') === 'code_only' ? <Shield className="h-3.5 w-3.5" /> : <Key className="h-3.5 w-3.5" />;

  const filteredCompanies = companies.filter((company) => {
    if (!companyFilter) return true;
    const q = companyFilter.toLowerCase();
    return company.name.toLowerCase().includes(q) || company.code.toLowerCase().includes(q);
  });

  // While the tier is still loading (null) we stay optimistic and let the user
  // try — the backend is the real gate and will return 403 if not allowed.
  const canCreateCompany =
    subscriptionTier == null ||
    (TIER_RANK[subscriptionTier] ?? 0) >= TIER_RANK[MIN_COMPANY_TIER];
  const upsellMessage = `Creating companies requires the ${MIN_COMPANY_TIER} plan or higher. Upgrade your subscription to enable it.`;

  // ── Branding field helper ──────────────────────────────────
  const BrandingFields: React.FC<{
    branding: CompanyBranding;
    onChange: (b: CompanyBranding) => void;
  }> = ({ branding, onChange }) => (
    <details className="rounded-lg border border-slate-200 bg-slate-50/70">
      <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700">
        <span className="flex items-center">
        <Palette className="h-4 w-4 mr-2 text-slate-500" />
        Advanced branding
        </span>
        <span className="text-xs font-medium text-slate-400">Optional</span>
      </summary>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-slate-200 bg-white p-4">
      {[
        { key: 'logo_url' as const, label: 'Logo URL', ph: 'https://.../logo.png' },
        { key: 'primary_color' as const, label: 'Primary Color', ph: '#4F46E5' },
        { key: 'secondary_color' as const, label: 'Secondary Color', ph: '#7C3AED' },
        { key: 'accent_color' as const, label: 'Accent Color', ph: '#10B981' },
        { key: 'welcome_text' as const, label: 'Welcome Text', ph: 'Welcome to Company' },
        { key: 'background_image_url' as const, label: 'Background Image URL', ph: 'https://.../bg.jpg' },
      ].map(({ key, label, ph }) => (
        <div key={key}>
          <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
          <input
            type="text"
            placeholder={ph}
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            value={branding[key] || ''}
            onChange={(e) =>
              onChange({
                ...branding,
                [key]: e.target.value || undefined,
              })
            }
          />
        </div>
      ))}
      </div>
    </details>
  );

  // ── feedback banner ────────────────────────────────────────
  const FeedbackBanner: React.FC = () => (
    <>
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 mb-4">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto p-1 hover:bg-red-100 rounded">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700 mb-4">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          {success}
          <button onClick={() => setSuccess('')} className="ml-auto p-1 hover:bg-emerald-100 rounded">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </>
  );


  // ══════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-indigo-600" />
        <p className="text-sm font-medium text-slate-400">Loading companies...</p>
      </div>
    );
  }

  // ── LIST VIEW ──────────────────────────────────────────────
  if (view.kind === 'list') {
    return (
      <>
        <ConfirmDialog confirmation={confirmation} />
        <div className="space-y-5">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900 flex items-center">
                <Building2 className="h-5 w-5 mr-2 text-slate-500" />
                Companies
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Manage company access, users and lock assignments from one place.
              </p>
            </div>
            <button
              onClick={() => {
                clearMessages();
                setView({ kind: 'create' });
              }}
              disabled={!canCreateCompany}
              title={canCreateCompany ? undefined : upsellMessage}
              className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-600"
            >
              <Plus className="h-4 w-4 mr-2" />
              New company
            </button>
          </div>

          {!canCreateCompany && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
              <CreditCard className="h-4 w-4 flex-shrink-0" />
              {upsellMessage}
            </div>
          )}

          {companies.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-10 text-center">
              <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-slate-800">No companies yet</h3>
              <p className="text-sm text-slate-500 mt-1 mb-5">
                Create a company to issue shared access without personal emails.
              </p>
              {canCreateCompany ? (
                <button
                  onClick={() => {
                    clearMessages();
                    setView({ kind: 'create' });
                  }}
                  className="inline-flex items-center px-4 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create company
                </button>
              ) : (
                <div className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg">
                  <CreditCard className="h-4 w-4 flex-shrink-0" />
                  {upsellMessage}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-slate-200 bg-slate-50/70">
                <div className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">{filteredCompanies.length}</span> of {companies.length} companies
                </div>
                <div className="relative w-full sm:max-w-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Filter className="h-4 w-4 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Filter by name or code"
                    className="w-full pl-10 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    value={companyFilter}
                    onChange={(e) => setCompanyFilter(e.target.value)}
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-white">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Company</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Users</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Login</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {filteredCompanies.map((company) => (
                      <tr key={company.id} className="hover:bg-slate-50/80">
                        <td className="px-5 py-4">
                          <div className="font-semibold text-slate-900">{company.name}</div>
                          <div className="mt-1 inline-flex items-center rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                            {company.code}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-600">
                          <span className="font-medium text-slate-900">{company.user_count ?? 0}</span>
                          <span className="text-slate-400"> / {company.max_users ?? 'unlimited'}</span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                            {loginModeIcon(company.login_mode)}
                            {loginModeLabel(company.login_mode)}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-semibold ${
                              company.is_active
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-red-50 text-red-700 border border-red-200'
                            }`}
                          >
                            {company.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-1.5">
                            <button
                              onClick={() => {
                                clearMessages();
                                setDetailTab('users');
                                setView({ kind: 'detail', company });
                              }}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                            >
                              Manage
                            </button>
                            <button
                              onClick={() => openEditView(company)}
                              className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg"
                              title="Edit company"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCompany(company)}
                              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                              title="Delete company"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredCompanies.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-5 py-10 text-center text-sm text-slate-500">
                          No companies match this filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  // ── CREATE COMPANY VIEW ────────────────────────────────────
  if (view.kind === 'create') {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <button
          onClick={() => setView({ kind: 'list' })}
          className="flex items-center text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to companies
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="h-1 bg-indigo-600" />
          <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center">
              <Plus className="h-5 w-5 mr-2 text-slate-500" />
              Create New Company
            </h2>
          </div>
          <form onSubmit={handleCreateCompany} className="p-6 space-y-5">
            <FeedbackBanner />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1.5">
                  Company Code *
                </label>
                <input
                  type="text"
                  placeholder="ACME2026"
                  maxLength={10}
                  className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm uppercase focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase().replace(/\s/g, ''))}
                  required
                />
                <p className="text-xs text-slate-400 mt-1">3-10 chars, letters, numbers, hyphens (no spaces)</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1.5">
                  Company Name *
                </label>
                <input
                  type="text"
                  placeholder="Acme Corporation"
                  maxLength={20}
                  className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  required
                />
                <p className="text-xs text-slate-400 mt-1">{newName.length}/20 characters</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">
                Login Mode *
              </label>
              <div className="flex gap-3">
                {(['password', 'code_only'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setNewLoginMode(mode)}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-all ${
                      newLoginMode === mode
                        ? 'bg-indigo-50 text-indigo-700 border-indigo-300 ring-2 ring-indigo-500/20'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {mode === 'password' ? (
                      <>
                        <Key className="h-4 w-4" />
                        Username + Password
                      </>
                    ) : (
                      <>
                        <Shield className="h-4 w-4" />
                        Code Only
                      </>
                    )}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1.5">
                {newLoginMode === 'password'
                  ? 'Users authenticate with a username and password'
                  : 'Users authenticate with a single access code - no password needed'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">
                Max Users <span className="font-normal text-slate-400">(leave empty for unlimited)</span>
              </label>
              <input
                type="number"
                min={1}
                placeholder="Unlimited"
                className="w-full sm:w-48 px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                value={newMaxUsers}
                onChange={(e) => setNewMaxUsers(e.target.value)}
              />
            </div>

            <BrandingFields branding={newBranding} onChange={setNewBranding} />

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setView({ kind: 'list' })}
                className="px-5 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={actionLoading}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-sm"
              >
                {actionLoading ? 'Creating...' : 'Create Company'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── EDIT COMPANY VIEW ──────────────────────────────────────
  if (view.kind === 'edit') {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <button
          onClick={() => setView({ kind: 'list' })}
          className="flex items-center text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to companies
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="h-1 bg-indigo-600" />
          <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center">
              <Settings className="h-5 w-5 mr-2 text-slate-500" />
              Edit Company - {view.company.code}
            </h2>
          </div>
          <form onSubmit={handleUpdateCompany} className="p-6 space-y-5">
            <FeedbackBanner />

            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">Company Name</label>
              <input
                type="text"
                maxLength={20}
                className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
              <p className="text-xs text-slate-400 mt-1">{editName.length}/20 characters</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">Max Users</label>
              <input
                type="number"
                min={1}
                placeholder="Unlimited"
                className="w-full sm:w-48 px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                value={editMaxUsers}
                onChange={(e) => setEditMaxUsers(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">
                Login Mode
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                {(['password', 'code_only'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setEditLoginMode(mode)}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-all ${
                      editLoginMode === mode
                        ? 'bg-indigo-50 text-indigo-700 border-indigo-300 ring-2 ring-indigo-500/20'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {loginModeIcon(mode)}
                    {loginModeLabel(mode)}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold text-slate-600">Active</label>
              <button
                type="button"
                onClick={() => setEditIsActive(!editIsActive)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  editIsActive ? 'bg-emerald-500' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                    editIsActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <BrandingFields branding={editBranding} onChange={setEditBranding} />

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setView({ kind: 'list' })}
                className="px-5 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={actionLoading}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 shadow-sm"
              >
                {actionLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── COMPANY DETAIL (USERS LIST) ────────────────────────────
  if (view.kind === 'detail') {
    const { company } = view;
    const isPasswordMode = (company.login_mode ?? 'password') === 'password';
    const detailTabs: Array<{ id: typeof detailTab; label: string; icon: React.ReactNode }> = [
      { id: 'users', label: 'Users', icon: <Users className="h-4 w-4" /> },
      { id: 'access', label: 'Group access', icon: <Shield className="h-4 w-4" /> },
      { id: 'nfc', label: 'NFC cards', icon: <CreditCard className="h-4 w-4" /> },
    ];

    return (
      <>
        <ConfirmDialog confirmation={confirmation} />
        <div className="space-y-5">
          <button
            onClick={() => setView({ kind: 'list' })}
            className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-800"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to companies
          </button>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-5">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-semibold text-slate-900">{company.name}</h2>
                    <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                      {company.code}
                    </span>
                    <span
                      className={`rounded-lg px-2 py-0.5 text-xs font-semibold ${
                        company.is_active
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}
                    >
                      {company.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-medium text-slate-500">Users</div>
                      <div className="font-semibold text-slate-900">{companyUsersTotal} / {company.max_users ?? 'unlimited'}</div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-medium text-slate-500">Login mode</div>
                      <div className="font-semibold text-slate-900">{loginModeLabel(company.login_mode)}</div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs font-medium text-slate-500">Company ID</div>
                      <div className="font-mono text-xs font-semibold text-slate-900 truncate">{company.id}</div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      clearMessages();
                      setView({ kind: 'addUser', company });
                    }}
                    className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add user
                  </button>
                  <button
                    onClick={() => openEditView(company)}
                    className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 shadow-sm"
                  >
                    <Edit3 className="h-4 w-4 mr-2" />
                    Edit company
                  </button>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-200 bg-slate-50/70 px-3">
              <div className="flex overflow-x-auto">
                {detailTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setDetailTab(tab.id)}
                    className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 ${
                      detailTab === tab.id
                        ? 'border-indigo-600 text-indigo-700'
                        : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <FeedbackBanner />

          {showImport && (
            <Modal
              open
              onClose={() => setShowImport(false)}
              title="Import users by CSV"
              icon={<Upload className="h-4 w-4 text-slate-500" />}
              maxWidth="2xl"
            >
              <p className="text-sm text-slate-500 mb-4">
                Validate first with dry run, then import when the file is clean.
              </p>
              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
                  <div className="flex items-start gap-2">
                    <FileText className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-700">Need a template?</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        Columns: <span className="font-mono">username</span>
                        {isPasswordMode && <>, <span className="font-mono">password</span></>}
                        , <span className="font-mono">first_name</span>, <span className="font-mono">last_name</span>, <span className="font-mono">role</span>.
                        {' '}Download an example and overwrite it with your own rows.
                        {isPasswordMode
                          ? ' This company uses username + password, so use the “with password” template.'
                          : ' This company uses access codes only, so passwords are not needed — use the “codes only” template.'}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => downloadCsvTemplate('company-users-with-password.csv', USER_IMPORT_EXAMPLE_WITH_PASSWORD)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg ${
                            isPasswordMode
                              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                              : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          <Download className="h-3.5 w-3.5" />
                          Example — with password
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadCsvTemplate('company-users-codes-only.csv', USER_IMPORT_EXAMPLE_CODE_ONLY)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg ${
                            isPasswordMode
                              ? 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                              : 'bg-indigo-600 text-white hover:bg-indigo-700'
                          }`}
                        >
                          <Download className="h-3.5 w-3.5" />
                          Example — codes only
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 lg:items-end">
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1.5">CSV file</label>
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      disabled={importLoading}
                      onChange={(e) => {
                        handleImportFileChange(e.target.files?.[0] || null);
                      }}
                      className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-slate-700 hover:file:bg-slate-200"
                    />
                    {importPreviewRows !== null && !importResult && (
                      <p className="mt-2 text-xs text-slate-500">
                        Selected CSV: {importPreviewRows} data rows
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleImportUsers(company.id)}
                    disabled={importLoading || actionLoading || !importFile}
                    className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {importLoading ? (
                      <span className="h-4 w-4 mr-2 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4 mr-2" />
                    )}
                    {importLoading ? (importDryRun ? 'Checking...' : 'Importing...') : (importDryRun ? 'Check CSV' : 'Import CSV')}
                  </button>
                </div>
                {(importLoading || importProgress > 0) && (
                  <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="font-medium text-indigo-900">
                        {importLoading
                          ? importStage === 'reading'
                            ? 'Reading CSV...'
                            : importStage === 'uploading'
                            ? 'Uploading CSV...'
                            : 'Processing CSV on server...'
                          : 'CSV finished'}
                        {importBatchLabel ? ` (${importBatchLabel})` : ''}
                      </span>
                      <span className="font-semibold text-indigo-700">
                        {importStage === 'processing' && importLoading ? 'Processing' : `${importProgress}%`}
                      </span>
                    </div>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white border border-indigo-100">
                      <div
                        className={`h-full rounded-full bg-indigo-600 transition-all duration-300 ${
                          importStage === 'processing' && importLoading ? 'animate-pulse' : ''
                        }`}
                        style={{ width: `${Math.max(importProgress, importLoading ? 8 : 0)}%` }}
                      />
                    </div>
                    <p className="mt-2 text-xs text-indigo-700">
                      {importStage === 'reading' && importLoading
                        ? `Preparing CSV batches of ${IMPORT_CHUNK_SIZE} rows.`
                        : importStage === 'processing' && importLoading
                        ? 'The file has uploaded. Waiting for validation/import result.'
                        : importLoading
                          ? 'Keep this page open until the result appears.'
                          : 'Result is shown below.'}
                    </p>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-3">
                  <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={importDryRun}
                      disabled={importLoading}
                      onChange={(e) => {
                        clearMessages();
                        setImportDryRun(e.target.checked);
                        setImportResult(null);
                        setImportProgress(0);
                        setImportStage('idle');
                        setImportBatchLabel('');
                      }}
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Dry run
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={importUpdateExisting}
                      disabled={importLoading}
                      onChange={(e) => {
                        clearMessages();
                        setImportUpdateExisting(e.target.checked);
                        setImportResult(null);
                        setImportProgress(0);
                        setImportStage('idle');
                        setImportBatchLabel('');
                      }}
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Update existing users
                  </label>
                </div>
                {importResult && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div><span className="block text-xs text-slate-500">Rows</span><span className="font-semibold text-slate-900">{importResult.total_rows}</span></div>
                      <div><span className="block text-xs text-slate-500">Created</span><span className="font-semibold text-slate-900">{importResult.created}</span></div>
                      <div><span className="block text-xs text-slate-500">Updated</span><span className="font-semibold text-slate-900">{importResult.updated}</span></div>
                      <div><span className="block text-xs text-slate-500">Skipped</span><span className="font-semibold text-slate-900">{importResult.skipped}</span></div>
                      <div><span className="block text-xs text-slate-500">Errors</span><span className="font-semibold text-slate-900">{importResult.errors.length}</span></div>
                    </div>
                    {importResult.errors.length > 0 && (
                      <div className="mt-3 max-h-36 overflow-y-auto rounded-lg border border-red-100 bg-white">
                        {importResult.errors.slice(0, 20).map((err, idx) => (
                          <div key={`${err.row}-${idx}`} className="px-3 py-2 text-xs text-red-700 border-b border-red-50 last:border-b-0">
                            Row {err.row} ({err.username || 'no username'}): {err.error}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Modal>
          )}

          {detailTab === 'users' && (
            <>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 bg-slate-50/70 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-base font-semibold text-slate-900">Company users</h3>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-500">
                      {companyUserSearch.trim()
                        ? `${companyUsers.length} of ${companyUsersTotal} matching`
                        : `${companyUsers.length} of ${companyUsersTotal} loaded`}
                    </span>
                    <button
                      onClick={() => {
                        clearMessages();
                        setShowImport(true);
                      }}
                      className="inline-flex items-center justify-center px-3 py-2 text-sm font-semibold bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 shadow-sm"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Import CSV
                    </button>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Filter className="h-4 w-4 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search users by username or name"
                    value={companyUserSearch}
                    onChange={(e) => setCompanyUserSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-white">
                    <tr>
                      <th className="py-3 px-5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Username</th>
                      <th className="py-3 px-5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Full name</th>
                      <th className="py-3 px-5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                      <th className="py-3 px-5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {companyUsers.map((u) => (
                      <tr key={u.id} className="hover:bg-slate-50/80">
                        <td className="py-3.5 px-5 text-sm font-semibold text-slate-900">{u.company_username}</td>
                        <td className="py-3.5 px-5 text-sm text-slate-600">{u.full_name || '-'}</td>
                        <td className="py-3.5 px-5 text-sm">
                          <span
                            className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold ${
                              u.company_role === 'admin'
                                ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                                : 'bg-slate-100 text-slate-700 border border-slate-200'
                            }`}
                          >
                            <Shield className="h-3 w-3" />
                            {u.company_role}
                          </span>
                        </td>
                        <td className="py-3.5 px-5 text-sm">
                          <div className="flex justify-end gap-1.5">
                            <button
                              onClick={() => {
                                clearMessages();
                                setAssignmentTab('groups');
                                setView({ kind: 'manageLocks', company, user: u });
                              }}
                              className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg"
                              title="Manage access"
                            >
                              <Settings size={17} />
                            </button>
                            {isPasswordMode && (
                              <button
                                onClick={() => {
                                  clearMessages();
                                  setResetUserId(u.id);
                                  setResetPassword('');
                                }}
                                className="p-2 text-slate-500 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg"
                                title="Reset password"
                              >
                                <Key size={17} />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteUser(company.id, u)}
                              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                              title="Delete user"
                            >
                              <Trash2 size={17} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {companyUsers.length === 0 && (
                      <tr>
                        <td colSpan={4} className="py-10 text-center text-slate-500 text-sm">
                          {companyUserSearch.trim()
                            ? 'No users match this search.'
                            : 'No users yet. Add the first user from the button above.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {companyUsersHasMore && (
                <div className="border-t border-slate-200 bg-slate-50/70 px-5 py-4 flex justify-center">
                  <button
                    type="button"
                    onClick={() => loadCompanyUsers(company.id, companyUserSearch, companyUsers.length, true)}
                    disabled={companyUsersLoading}
                    className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                  >
                    {companyUsersLoading ? 'Loading...' : 'Load more users'}
                  </button>
                </div>
              )}
            </div>
            </>
          )}

          {detailTab === 'access' && (
            <div className="space-y-5">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50/70">
                  <h3 className="text-base font-semibold text-slate-900">Bulk group access</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Assign or remove one group for every user in this company.
                  </p>
                </div>
                <div className="p-5 space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_auto] gap-3 lg:items-end">
                    <div>
                      <label className="block text-sm font-semibold text-slate-600 mb-1.5">Group</label>
                      <select
                        value={bulkGroupId}
                        onChange={(e) => setBulkGroupId(e.target.value)}
                        className="w-full px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                      >
                        <option value="">Select a group</option>
                        {availableGroups.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.name} ({group.lock_count} locks)
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAssignGroupToAllUsers(company.id)}
                      disabled={actionLoading || !bulkGroupId}
                      className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Assign to all
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUnassignGroupFromAllUsers(company.id)}
                      disabled={actionLoading || !bulkGroupId}
                      className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove from all
                    </button>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={bulkAllowSettings}
                      onChange={(e) => setBulkAllowSettings(e.target.checked)}
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Allow settings changes when assigning
                  </label>
                  {availableGroups.length === 0 && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      No groups found for this company yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {detailTab === 'nfc' && <NfcCardsManager companyId={company.id} />}

          {resetUserId && isPasswordMode && (
            <Modal
              open
              onClose={() => {
                setResetUserId(null);
                setResetPassword('');
              }}
              title="Reset password"
              icon={<Key className="h-4 w-4 text-indigo-500" />}
              headerAccent="bg-indigo-50/50"
              maxWidth="md"
              footer={
                <>
                  <button
                    onClick={() => {
                      setResetUserId(null);
                      setResetPassword('');
                    }}
                    className="px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleResetPassword(company.id)}
                    disabled={actionLoading || !resetPassword}
                    className="px-4 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {actionLoading ? 'Resetting...' : 'Reset password'}
                  </button>
                </>
              }
            >
              <div className="space-y-4">
                <p className="text-sm text-slate-500">
                  User: <span className="font-semibold text-slate-800">{companyUsers.find((u) => u.id === resetUserId)?.company_username}</span>
                </p>
                <div className="relative">
                  <input
                    type={showResetPassword ? 'text' : 'password'}
                    placeholder="New password"
                    className="w-full px-4 py-2.5 pr-10 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                    aria-label={showResetPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showResetPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </Modal>
          )}
        </div>
      </>
    );
  }

  // ── ADD USER VIEW ──────────────────────────────────────────
  if (view.kind === 'addUser') {
    const { company } = view;
    const isPasswordMode = (company.login_mode ?? 'password') === 'password';
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <button
          onClick={() => setView({ kind: 'detail', company })}
          className="flex items-center text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to {company.name} users
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="h-1 bg-indigo-500" />
          <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center">
              <UserPlus className="h-5 w-5 mr-2 text-indigo-500" />
              Add User to {company.name}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {isPasswordMode ? (
                <>Login: <span className="font-mono text-slate-700">{company.code}</span> + username + password</>
              ) : (
                <>Login: <span className="font-mono text-slate-700">{company.code}</span> + access code only</>
              )}
            </p>
          </div>
          <form onSubmit={handleCreateUser} className="p-6 space-y-5">
            <FeedbackBanner />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1.5">
                  {isPasswordMode ? 'Username *' : 'Access Code *'}
                </label>
                <input
                  type="text"
                  placeholder={isPasswordMode ? 'john.doe' : 'acc-001'}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm lowercase focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value.toLowerCase())}
                  required
                />
                <p className="text-xs text-slate-400 mt-1">3-50 chars, lowercase, letters, numbers, dots, hyphens</p>
              </div>
              {isPasswordMode && (
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1.5">Password *</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Min 8 chars, 1 uppercase, 1 number"
                    className="w-full px-4 py-2.5 pr-10 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1.5">First Name</label>
                <input
                  type="text"
                  placeholder="John"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  value={newFirstName}
                  onChange={(e) => setNewFirstName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1.5">Last Name</label>
                <input
                  type="text"
                  placeholder="Doe"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  value={newLastName}
                  onChange={(e) => setNewLastName(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">Role</label>
              <div className="flex gap-3">
                {(['user', 'admin'] as const).map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setNewRole(r)}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-xl border transition-all ${
                      newRole === r
                        ? r === 'admin'
                          ? 'bg-violet-50 text-violet-700 border-violet-300 ring-2 ring-violet-500/20'
                          : 'bg-indigo-50 text-indigo-700 border-indigo-300 ring-2 ring-indigo-500/20'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <Shield className="h-4 w-4" />
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1.5">
                Admin users can create/delete other users in this company
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setView({ kind: 'detail', company })}
                className="px-5 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={actionLoading}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50 shadow-sm"
              >
                {actionLoading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── MANAGE LOCKS VIEW ──────────────────────────────────────
  if (view.kind === 'manageLocks') {
    const { company, user: targetUser } = view;
    const assignedUnitIds = new Set(assignedUnits.map((a) => a.unit_id));
    const assignedGroupIds = new Set(assignedGroups.map((a) => a.group_id));
    const unassignedLocks = availableLocks.filter((l) => !assignedUnitIds.has(l.id));
    const unassignedGroups = availableGroups.filter((g) => !assignedGroupIds.has(g.id));

    const filterLower = lockAssignFilter.toLowerCase();
    const filteredAssignedUnits = assignedUnits.filter(a =>
      !lockAssignFilter || a.lock_name.toLowerCase().includes(filterLower) || a.mac_address?.toLowerCase().includes(filterLower)
    );
    const filteredAssignedGroups = assignedGroups.filter(a =>
      !lockAssignFilter || a.group_name.toLowerCase().includes(filterLower)
    );
    const filteredUnassignedLocks = unassignedLocks.filter(l =>
      !lockAssignFilter || l.name.toLowerCase().includes(filterLower) || l.mac_address?.toLowerCase().includes(filterLower)
    );
    const filteredUnassignedGroups = unassignedGroups.filter(g =>
      !lockAssignFilter || g.name.toLowerCase().includes(filterLower)
    );
    // Full universe (assigned + available) for the switch-based lists.
    const filteredGroupRows = availableGroups.filter(g =>
      !lockAssignFilter || g.name.toLowerCase().includes(filterLower)
    );
    const filteredLockRows = availableLocks.filter(l =>
      !lockAssignFilter || l.name.toLowerCase().includes(filterLower) || l.mac_address?.toLowerCase().includes(filterLower)
    );

    const assignmentTabs = [
      { id: 'groups' as const, label: 'Groups', assigned: filteredAssignedGroups.length, available: filteredUnassignedGroups.length },
      { id: 'locks' as const, label: 'Individual locks', assigned: filteredAssignedUnits.length, available: filteredUnassignedLocks.length },
    ];

    return (
      <>
        <ConfirmDialog confirmation={confirmation} />
        <div className="space-y-5">
          <button
            onClick={() => setView({ kind: 'detail', company })}
            className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-800"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to {company.name}
          </button>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <Settings className="h-5 w-5 text-slate-500" />
                  Access for {targetUser.company_username}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {targetUser.full_name || targetUser.company_username} in {company.name}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-xs font-medium text-slate-500">Groups</div>
                  <div className="font-semibold text-slate-900">{assignedGroups.length} assigned</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-xs font-medium text-slate-500">Locks</div>
                  <div className="font-semibold text-slate-900">{assignedUnits.length} assigned</div>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-200 bg-slate-50/70 px-3">
              <div className="flex overflow-x-auto">
                {assignmentTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setAssignmentTab(tab.id)}
                    className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 ${
                      assignmentTab === tab.id
                        ? 'border-indigo-600 text-indigo-700'
                        : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    {tab.label}
                    <span className="rounded bg-white border border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-500">
                      {tab.assigned}/{tab.assigned + tab.available}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <FeedbackBanner />

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-4 w-4 text-slate-400" />
              </div>
              <input
                type="text"
                placeholder={assignmentTab === 'groups' ? 'Filter groups' : 'Filter locks or MAC addresses'}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                value={lockAssignFilter}
                onChange={(e) => setLockAssignFilter(e.target.value)}
              />
            </div>
          </div>

          {locksLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-indigo-600" />
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 bg-slate-50/70">
                <h3 className="text-base font-semibold text-slate-900">
                  {assignmentTab === 'groups' ? 'Groups' : 'Individual locks'}
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Toggle access on or off. When access is on, you can also allow settings changes.
                </p>
              </div>
              <div className="divide-y divide-slate-100">
                {assignmentTab === 'groups' ? (
                  filteredGroupRows.length === 0 ? (
                    <div className="py-10 text-center text-sm text-slate-500">
                      {availableGroups.length === 0 ? 'No groups in this company yet.' : 'No groups match this filter.'}
                    </div>
                  ) : (
                    filteredGroupRows.map((group) => {
                      const assigned = assignedGroups.find((a) => a.group_id === group.id);
                      return (
                        <div key={group.id} className="flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-slate-50">
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 truncate">{group.name}</div>
                            <div className="text-xs text-slate-500">{group.lock_count} locks</div>
                          </div>
                          <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
                            {assigned && (
                              <button
                                onClick={() => handleToggleSettings(company.id, targetUser.id, assigned.assignment_id, assigned.allow_settings_changes, 'group')}
                                className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium ${
                                  assigned.allow_settings_changes
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-white text-slate-500 border-slate-200'
                                }`}
                                title={assigned.allow_settings_changes ? 'Can edit settings' : 'Cannot edit settings'}
                              >
                                {assigned.allow_settings_changes ? <ToggleRight className="h-3.5 w-3.5" /> : <ToggleLeft className="h-3.5 w-3.5" />}
                                Settings
                              </button>
                            )}
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium w-20 text-right ${assigned ? 'text-indigo-600' : 'text-slate-400'}`}>
                                {assigned ? 'Assigned' : 'No access'}
                              </span>
                              <Switch
                                on={!!assigned}
                                disabled={actionLoading}
                                label={`Access to ${group.name}`}
                                onClick={() =>
                                  assigned
                                    ? handleUnassignGroup(company.id, targetUser.id, assigned.assignment_id, group.name)
                                    : handleAssignGroup(company.id, targetUser.id, group.id, false)
                                }
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )
                ) : (
                  filteredLockRows.length === 0 ? (
                    <div className="py-10 text-center text-sm text-slate-500">
                      {availableLocks.length === 0 ? 'No locks found in your account.' : 'No locks match this filter.'}
                    </div>
                  ) : (
                    filteredLockRows.map((lock) => {
                      const assigned = assignedUnits.find((a) => a.unit_id === lock.id);
                      return (
                        <div key={lock.id} className="flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-slate-50">
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 truncate">{lock.name}</div>
                            <div className="text-xs font-mono text-slate-500 truncate">{lock.mac_address}</div>
                          </div>
                          <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
                            {assigned && (
                              <button
                                onClick={() => handleToggleSettings(company.id, targetUser.id, assigned.assignment_id, assigned.allow_settings_changes, 'unit')}
                                className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium ${
                                  assigned.allow_settings_changes
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-white text-slate-500 border-slate-200'
                                }`}
                                title={assigned.allow_settings_changes ? 'Can edit settings' : 'Cannot edit settings'}
                              >
                                {assigned.allow_settings_changes ? <ToggleRight className="h-3.5 w-3.5" /> : <ToggleLeft className="h-3.5 w-3.5" />}
                                Settings
                              </button>
                            )}
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium w-20 text-right ${assigned ? 'text-indigo-600' : 'text-slate-400'}`}>
                                {assigned ? 'Assigned' : 'No access'}
                              </span>
                              <Switch
                                on={!!assigned}
                                disabled={actionLoading}
                                label={`Access to ${lock.name}`}
                                onClick={() =>
                                  assigned
                                    ? handleUnassignLock(company.id, targetUser.id, assigned.assignment_id, lock.name)
                                    : handleAssignLock(company.id, targetUser.id, lock.id, false)
                                }
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  return null;
};

export default CompanyManagement;
